import { router, ROUTES } from '@/router.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { captureScreenshotAction } from '@/actions/run/CaptureScreenshotAction.ts';
import { showScreenshotPreviewAction } from '@/actions/run/ShowScreenshotPreviewAction.ts';

export const MAX_SCREEN_CAPTURES = 3;

export const attachScreenshotAction = async (): Promise<boolean> => {
    const route = router.currentRoute.value;
    const clientStore = useClientStore();
    const settingsStore = useSettingsStore();
    const sessionId = String(route.params.sessionId ?? '');
    const profileId = String(route.params.profileConfigId ?? '');
    const stepId = String(route.params.stepConfigId ?? '');

    if (
        route.name !== ROUTES.runIndex ||
        !sessionId ||
        clientStore.activeSessionId !== sessionId ||
        clientStore.activeProfileId !== profileId ||
        settingsStore.selectedProfileId !== profileId
    ) {
        return false;
    }

    const hasImagePrompt = Object.values(PromptConfigService.getAllPromptConfigs()).some(
        (prompt) => prompt.stepId === stepId && prompt.includeScreenCapture,
    );
    if (!hasImagePrompt) {
        createTextToast(
            'No screenshot prompts in this step',
            'Move to a step with an image prompt before attaching another screenshot.',
            'destructive',
            true,
        );
        return true;
    }

    const existingCaptures = clientStore.screenCapturesBySession[sessionId] ?? [];
    if (existingCaptures.length >= MAX_SCREEN_CAPTURES) {
        createTextToast(
            'Screenshot limit reached',
            `All ${MAX_SCREEN_CAPTURES} screenshot slots are in use. Start a new run to attach more.`,
            'destructive',
            true,
        );
        return true;
    }

    const screenCaptureData = await captureScreenshotAction(sessionId);
    if (!screenCaptureData) return true;

    const nextCaptures = [...existingCaptures, screenCaptureData];
    const nextRevision = (clientStore.screenCaptureRevisions[sessionId] ?? 0) + 1;
    clientStore.$patch({
        screenCapturesBySession: {
            ...clientStore.screenCapturesBySession,
            [sessionId]: nextCaptures,
        },
        screenCaptureRevisions: {
            ...clientStore.screenCaptureRevisions,
            [sessionId]: nextRevision,
        },
    });

    showScreenshotPreviewAction(screenCaptureData, nextCaptures.length);

    await router.replace({
        name: ROUTES.runIndex,
        params: route.params,
        query: { ...route.query, captureRevision: String(nextRevision) },
    });

    return true;
};
